import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { thematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import type { ThematicBucket } from "../types";

type Status = "all" | "on" | "off";

/** One goal, flattened out of the catalogue with what the filters need. */
type Row = {
  id: string;
  name: string;
  issue: string;
  thematic: string;
  cssClass?: string | null;
};

/** The needle marked inside the haystack, for the search hit. */
function Highlighted({ value, needle }: { value: string; needle: string }) {
  if (!needle) return <>{value}</>;
  const at = value.toLowerCase().indexOf(needle);
  if (at === -1) return <>{value}</>;
  return (
    <>
      {value.slice(0, at)}
      <mark className="rounded-sm bg-attention/25 text-inherit">
        {value.slice(at, at + needle.length)}
      </mark>
      {value.slice(at + needle.length)}
    </>
  );
}

/**
 * The goal picker: one flat list of goals, with search and filters.
 *
 * The selection is made at goal level, so the goal is the row. The thématique
 * and the enjeu are what they actually are on this screen — filters — rather
 * than a nesting to walk before reaching a checkbox, and their counts ride on
 * the filter controls themselves so no number is written twice.
 *
 * "Select the ones shown" is a shortcut over the same checkboxes and nothing
 * more: what is submitted is always the individual goal ids, so unticking one
 * afterwards leaves the rest. Nothing is stored at thématique or enjeu level.
 *
 * The Django picker was reshaped the same way and reads the same; the enjeu
 * filter there is a `<select data-searchable>` enhanced by
 * `searchable-select.js`, which is this `SearchableSelect`'s counterpart.
 */
export function GoalPicker({
  tree,
  selected,
  onChange,
  initialGoalIds,
  error,
}: {
  tree: ThematicBucket[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /**
   * What "restore the starting selection" restores: the demonstration set on a
   * create, the group's own set on an edit. Handing back the demonstration set
   * on an edit would discard the group's goals under a button that reads like
   * an undo.
   */
  initialGoalIds: string[];
  error?: string;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [issue, setIssue] = useState("");
  const [status, setStatus] = useState<Status>("all");
  const [thematics, setThematics] = useState<Set<string>>(new Set());

  const rows = useMemo<Row[]>(
    () =>
      tree.flatMap(({ thematic, issues }) =>
        issues.flatMap(({ issue: bucketIssue, goals }) =>
          goals.map((goal) => ({
            id: goal.id,
            name: goal.name,
            issue: bucketIssue.name,
            thematic: thematic.name,
            cssClass: thematic.css_class,
          })),
        ),
      ),
    [tree],
  );

  const issueOptions = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => counts.set(row.issue, (counts.get(row.issue) ?? 0) + 1));
    return [...counts.entries()].map(([name, count]) => ({
      id: name,
      name: `${name} (${count})`,
    }));
  }, [rows]);

  const thematicFacets = useMemo(() => {
    const order: string[] = [];
    const byName = new Map<string, { total: number; cssClass?: string | null }>();
    rows.forEach((row) => {
      if (!byName.has(row.thematic)) {
        byName.set(row.thematic, { total: 0, cssClass: row.cssClass });
        order.push(row.thematic);
      }
      byName.get(row.thematic)!.total += 1;
    });
    return order.map((name) => ({
      name,
      total: byName.get(name)!.total,
      cssClass: byName.get(name)!.cssClass,
      selected: rows.filter((r) => r.thematic === name && selected.has(r.id)).length,
    }));
  }, [rows, selected]);

  const needle = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      rows.filter((row) => {
        if (thematics.size && !thematics.has(row.thematic)) return false;
        if (issue && row.issue !== issue) return false;
        if (status === "on" && !selected.has(row.id)) return false;
        if (status === "off" && selected.has(row.id)) return false;
        if (
          needle &&
          !row.name.toLowerCase().includes(needle) &&
          !row.issue.toLowerCase().includes(needle)
        )
          return false;
        return true;
      }),
    [rows, thematics, issue, status, needle, selected],
  );

  const filtering =
    Boolean(needle) || Boolean(issue) || status !== "all" || thematics.size > 0;
  const shownSelected = visible.filter((row) => selected.has(row.id)).length;
  const emptyThematics = thematicFacets.filter((f) => f.selected === 0);
  const atInitial =
    selected.size === initialGoalIds.length &&
    initialGoalIds.every((id) => selected.has(id));

  const setMany = (ids: string[], on: boolean) => {
    const next = new Set(selected);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    onChange(next);
  };

  const toggleThematic = (name: string) => {
    const next = new Set(thematics);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setThematics(next);
    // An enjeu the chips no longer admit would filter the list to nothing.
    if (issue && next.size && !rows.some((r) => r.issue === issue && next.has(r.thematic)))
      setIssue("");
  };

  const statuses: Status[] = ["all", "on", "off"];

  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-medium text-foreground">
          {t("formationGroups.form.goalsLabel")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("formationGroups.form.goalsHelp")}
        </p>
        {error ? (
          <p role="alert" className="mt-1 text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-border px-4 py-3">
          <p className="whitespace-nowrap">
            <span className="text-2xl font-bold tabular-nums">{selected.size}</span>
            <span className="ml-1 text-sm text-muted-foreground">
              {t("formationGroups.form.ofTotal", { total: rows.length })}
            </span>
          </p>
          {emptyThematics.length ? (
            <p className="ml-auto text-sm font-semibold text-attention-ink">
              {t("formationGroups.form.emptyThematics", {
                thematics: emptyThematics.map((f) => f.name).join(", "),
              })}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2 border-b border-border bg-muted/40 px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("formationGroups.form.searchGoalPlaceholder")}
              aria-label={t("formationGroups.form.searchGoal")}
              className="h-9 min-w-0 flex-1 rounded-md border border-input bg-card px-3 text-sm text-foreground"
            />
            {/* The shared searchable select, as on the Django side: 33 enjeux
                with several near-identical names. */}
            <SearchableSelect
              value={issue}
              onChange={setIssue}
              options={issueOptions}
              placeholder={t("formationGroups.form.allIssues")}
              searchPlaceholder={t("formationGroups.form.searchIssue")}
              emptyLabel={t("formationGroups.form.noIssueMatches")}
              className="w-64"
            />
            <div
              role="group"
              aria-label={t("formationGroups.form.filterByState")}
              className="inline-flex overflow-hidden rounded-md border border-input"
            >
              {statuses.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setStatus(value)}
                  aria-pressed={status === value}
                  className={cn(
                    "border-r border-input px-3 py-1.5 text-xs last:border-r-0",
                    status === value
                      ? "bg-foreground font-semibold text-background"
                      : "bg-card text-muted-foreground",
                  )}
                >
                  {t(`formationGroups.form.status.${value}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("formationGroups.form.thematic")}
            </span>
            {thematicFacets.map((facet) => {
              const theme = thematicClasses(facet.cssClass);
              const on = thematics.has(facet.name);
              return (
                <button
                  key={facet.name}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleThematic(facet.name)}
                  className={cn(
                    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold tabular-nums",
                    theme.border,
                    on ? cn(theme.solid, theme.onSolid) : "bg-card " + theme.ink,
                    facet.selected === 0 && !on && "opacity-60",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "size-2 shrink-0 rounded-full",
                      on ? "bg-current" : theme.solid,
                    )}
                  />
                  {facet.name} {facet.selected}/{facet.total}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-info/30 bg-info/10 px-4 py-2 text-sm text-info-ink">
          <span>
            {t("formationGroups.form.shown", { count: visible.length })}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setMany(visible.map((r) => r.id), true)}
          >
            {filtering
              ? t("formationGroups.form.selectShown", { count: visible.length })
              : t("formationGroups.form.selectAll")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setMany(visible.map((r) => r.id), false)}
          >
            {filtering
              ? t("formationGroups.form.clearShown", { count: visible.length })
              : t("formationGroups.form.clearAll")}
          </Button>
          {filtering ? (
            <span className="ml-auto tabular-nums">
              {t("formationGroups.form.scope", {
                selected: shownSelected,
                shown: visible.length,
              })}
            </span>
          ) : null}
          {/* The one control here that ignores the filters, so it never speaks
              of "shown" — hence the rule separating it. Disabled while nothing
              has changed, which also answers "have I touched anything?". */}
          <button
            type="button"
            disabled={atInitial}
            title={
              atInitial
                ? t("formationGroups.form.resetNoop")
                : t("formationGroups.form.resetHint")
            }
            onClick={() => onChange(new Set(initialGoalIds))}
            className={cn(
              "border-l border-info/30 pl-3 text-sm underline underline-offset-2",
              filtering ? "ml-3" : "ml-auto",
              atInitial && "cursor-default no-underline opacity-45",
            )}
          >
            {t("formationGroups.form.reset", { count: initialGoalIds.length })}
          </button>
        </div>

        <CardContent className="max-h-[28rem] overflow-y-auto p-0">
          {visible.length ? (
            <ul>
              {visible.map((row) => {
                const theme = thematicClasses(row.cssClass);
                return (
                  <li key={row.id} className="border-b border-border last:border-b-0">
                    <label className="flex cursor-pointer items-start gap-3 px-4 py-2 hover:bg-muted/50">
                      {/* Named by the goal and described by where it sits, so
                          the accessible name is the thing being chosen rather
                          than the goal plus its breadcrumb. */}
                      <input
                        type="checkbox"
                        className="mt-1 size-4 shrink-0 accent-primary"
                        checked={selected.has(row.id)}
                        aria-labelledby={`goal-name-${row.id}`}
                        aria-describedby={`goal-meta-${row.id}`}
                        onChange={() => setMany([row.id], !selected.has(row.id))}
                      />
                      <span className="min-w-0">
                        <span
                          id={`goal-name-${row.id}`}
                          className={cn(
                            "block text-sm leading-snug text-foreground",
                            selected.has(row.id) && "font-semibold",
                          )}
                        >
                          <Highlighted value={row.name} needle={needle} />
                        </span>
                        <span
                          id={`goal-meta-${row.id}`}
                          className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"
                        >
                          <span
                            aria-hidden
                            className={cn("size-2 shrink-0 rounded-full", theme.solid)}
                          />
                          {row.thematic} ·{" "}
                          <Highlighted value={row.issue} needle={needle} />
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t("formationGroups.form.noGoalMatches")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
